import React, { useEffect } from 'react';
import { Outlet, useNavigate, useOutlet, useLocation } from 'react-router-dom';
import { Notifications } from '@mantine/notifications';
import { ThemeProvider } from 'styled-components';
import Layout from '@hud/web-components/Layout/Layout';
import { darkColors } from '@hud/web-components/theme/colors';
import useAnalytics from '../hooks/useAnalytics';
import { useAuth } from '../hooks/useAuth';
import ClientProvider from '../hooks/useClient';
import { useConfig } from '../hooks/useConfig';
import useAccountInfo from '../hooks/useAccountInfo';
import { UserPreferencesProvider } from '../hooks/useUserPreferences';
import { EnvironmentProvider } from '../hooks/useEnvironment';
import { TimeFrameProvider } from '../hooks/useTimeFrame';
import usePlugSDK from '../hooks/usePlugSDK';
import { TierBanner } from '../components/TierBanner/TierBanner';

interface AuthenticatedContentProps {
  token: string;
}

// Create a separate component that uses hooks dependent on ClientProvider
const AuthenticatedContent = ({ token }: AuthenticatedContentProps) => {
  const navigate = useNavigate();
  const { data: accountInfo, isLoading } = useAccountInfo();
  const location = useLocation();

  useEffect(() => {
    if (isLoading || !accountInfo) {
      return;
    }

    if (token && !isLoading && (!accountInfo.companyName || !accountInfo.hasMetrics)) {
      // Explicitly navigate this way instead of using router navigation to prevent race conditions
      window.location.href = `/setup${location.search}`;
    }
  }, [accountInfo, isLoading, navigate, location.search]);

  // TODO: Temporary fix for the URL issue until extensions will be updated, VSC 1.2.11 and JB 1.0.18
  useEffect(() => {
    let updatedSearch = location.search;

    if (updatedSearch.includes('orderBy=metrics_normalizedRuns.value')) {
      updatedSearch = updatedSearch.replace(
        'orderBy=metrics_normalizedRuns.value',
        'orderBy=normalizedRuns.value',
      );
    }

    if (updatedSearch.includes('orderBy=metrics_durationP90.value')) {
      updatedSearch = updatedSearch.replace(
        'orderBy=metrics_durationP90.value',
        'orderBy=durationP90.value',
      );
    }

    if (updatedSearch !== location.search) {
      navigate(location.pathname + updatedSearch, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  // showing top banner for free tiers, and for enterprise tiers that have throttled sessions
  const showTopBanner =
    accountInfo?.tier === 'free' ||
    (accountInfo?.tierData?.throttledSessions !== undefined &&
      accountInfo?.tierData?.throttledSessions > 0);

  return (
    <UserPreferencesProvider>
      {accountInfo && showTopBanner && <TierBanner accountInfo={accountInfo} />}
      <TimeFrameProvider>
        <EnvironmentProvider>
          <Notifications />
          <Layout.Container className={showTopBanner ? 'topBanner' : ''}>
            <Outlet />
          </Layout.Container>
        </EnvironmentProvider>
      </TimeFrameProvider>
    </UserPreferencesProvider>
  );
};

const Root = () => {
  const location = useLocation();
  const outlet = useOutlet();
  const navigate = useNavigate();
  const { isLoading, token } = useAuth();
  const config = useConfig();
  const analytics = useAnalytics();
  usePlugSDK();

  useEffect(() => {
    if (outlet || !token || isLoading) {
      return;
    }

    navigate('/explore/' + location.search, { replace: true });
  }, [outlet, navigate, location.search]);

  useEffect(() => {
    analytics.pageview();
  }, [analytics, location.pathname]);

  return isLoading || !token ? ( // prevent flashing
    <ThemeProvider theme={darkColors}>
      <div></div>
    </ThemeProvider>
  ) : (
    <ClientProvider
      url={config.API_BASE_URL}
      detectionServiceUrl={config.DETECTION_SERVICE_URL}
      token={token}
    >
      <AuthenticatedContent token={token} />
    </ClientProvider>
  );
};

export default Root;
